import { useState } from "react";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import type { ManagedUser } from "./types";
import { apiErrorMessage, extendTrial, type Trial } from "./trials-api";
import { TrialConfirmDialog } from "./TrialConfirmDialog";
import { addDays, fmtDate, fmtDateFull } from "./trial-dates";

type Props = {
  user: ManagedUser | null;
  trial: Trial | null;
  additionalDays: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const ConfirmExtendDialog = ({
  user,
  trial,
  additionalDays,
  open,
  onClose,
  onSuccess,
}: Props) => {
  const [submitting, setSubmitting] = useState(false);

  if (!user || !trial) return null;

  const newEndIso = addDays(new Date(trial.trial_end), additionalDays).toISOString();
  const newTotal = trial.length_days + additionalDays;
  const numericId = parseInt(user.id, 10);

  const handleConfirm = async () => {
    if (isNaN(numericId)) return;
    setSubmitting(true);
    try {
      await extendTrial(numericId, additionalDays);
      toast.success(
        `Trial extended by ${additionalDays} days · new end ${fmtDate(newEndIso)}`,
      );
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to extend trial"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TrialConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      submitting={submitting}
      icon={Clock}
      title="Extend trial?"
      description={`${user.name ?? user.email}${user.name ? ` (${user.email})` : ""}`}
      confirmLabel="Confirm extension"
      submittingLabel="Extending…"
      helperText="The teacher will see the updated countdown immediately."
    >
      <div className="rounded-2xl border border-lime-600/20 bg-lime-600/10 overflow-hidden text-sm">
        <div className="flex justify-between items-center px-5 py-3.5 border-b border-lime-600/15">
          <span className="text-muted-foreground">Currently ends</span>
          <span className="font-semibold">{fmtDate(trial.trial_end)}</span>
        </div>
        <div className="flex justify-between items-center px-5 py-3.5 border-b border-lime-600/15">
          <span className="text-muted-foreground">Extending by</span>
          <span className="font-semibold">{additionalDays} days</span>
        </div>
        <div className="flex justify-between items-center px-5 py-3.5 border-b border-lime-600/15">
          <span className="text-muted-foreground">New end date</span>
          <span className="font-semibold text-lime-900">{fmtDateFull(newEndIso)}</span>
        </div>
        <div className="flex justify-between items-center px-5 py-3.5">
          <span className="text-muted-foreground">New total length</span>
          <span className="font-semibold">{newTotal} days</span>
        </div>
      </div>
    </TrialConfirmDialog>
  );
};
